/* eslint-disable @typescript-eslint/no-explicit-any */
import 'dayjs/locale/fr';
import React, {useCallback, useEffect, useState} from 'react';

import dayjs from 'dayjs';
import {
  AgeCategory,
  countPersonsInAgeRanges,
  getOldestDate,
} from '../../common/utils/functions';
import {
  showErrorValuesDefault,
  showErrorValuesSubmit,
} from '../../data/constants/Assurance/AssuranceVoyage/devis';
import {
  dataConfig,
  initModalData,
} from '../../data/constants/Assurance/AssuranceVoyage/souscription';
import {ApiResponse} from '../../data/types/interface/api';
import {
  CategorieVoyage,
  IError,
  SubmitDevisVoyage,
} from '../../data/types/interface/Assurance/AssuranceVoyage/devis';
import {ModalDataDTO} from '../../data/types/interface/Assurance/AssuranceVoyage/souscription';
import {IPostDevis, VoyageServices} from '../../services/applicatif/voyages';

// dayjs.extend(customParseFormat);
export interface Destination {
  id: number;
  value: string;
  categorie: string;
  categorieLabel: string;
  label: string;
  limit: number;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type

export const useFormDevis = (
  datas: SubmitDevisVoyage,
  setDatas: (value: SubmitDevisVoyage) => void,
  Validate: any,
  onSave: (params: IPostDevis & SubmitDevisVoyage) => void,
  onCancel: () => void,
  listCategories: Array<CategorieVoyage>,
) => {
  const [showErrors, setShowErrors] = useState(showErrorValuesDefault);
  const errors = React.useMemo<IError>(() => Validate(datas), [datas]);
  const [destinationsPerCategory, setDestinationsPerCategory] = useState<
    Record<string, Destination[]>
  >({});
  const {listDestinations: getDestinations} = VoyageServices();
  const [listDestinations, setListDestinations] = useState<Destination[]>([]);

  const getDestinationsOfCategory = useCallback(
    async (category: string) => {
      if (destinationsPerCategory[category]) {
        return destinationsPerCategory[category];
      } else {
        const res: ApiResponse<Destination[]> = await getDestinations(category);
        return res.data;
      }
    },
    [destinationsPerCategory],
  );

  useEffect(() => {
    if (datas.categoryCode != '') {
      getDestinationsOfCategory(datas.categoryCode).then(destinations => {
        setListDestinations(() => destinations || []);
        setDestinationsPerCategory(prev => ({
          ...prev,
          [datas.categoryCode]: destinations,
        }));
      });
    } else {
      setListDestinations(() => []);
    }
  }, [datas.categoryCode]);

  const [modalData, setModalData] = useState<ModalDataDTO>({...initModalData});
  const handleChange = (name: string, value: any, fired: boolean) => {
    const _value = value;
    if (value === 'INVALID INPUT') {
      setDatas({...datas, [name]: 'INVALID INPUT'});
    } else {
      if (name === 'categoryCode') {
        setDatas({...datas, [name]: _value, destinationCode: ''});
      } else {
        setDatas({...datas, [name]: _value});
      }
    }

    setShowErrors({...showErrors, [name]: value !== fired});
  };

  const noError = useCallback(() => {
    let bNoError = true;
    Object.entries(errors).forEach(element => {
      if (element[1] !== '' && element[0] !== 'acceptedCondition') {
        bNoError = false;
      }
    });
    return bNoError;
  }, [errors]);

  const handleSubmit = () => {
    setShowErrors(showErrorValuesSubmit);
    if (noError()) {
      const categorizedAges = datas.listNaissance.length
        ? countPersonsInAgeRanges(
            datas.listNaissance,
            {
              childrens: {
                min: dataConfig.ageGap.enfant.min,
                max: dataConfig.ageGap.enfant.max,
              },
              adults: {
                min: dataConfig.ageGap.adult.min,
                max: dataConfig.ageGap.adult.max,
              },
              seniors: {
                min: dataConfig.ageGap.senior.min,
                max: dataConfig.ageGap.senior.max,
              },
            },
            'value',
            ['DD/MM/YYYY', 'YYYY-MM-DD', '[date]'],
          )
        : {childrens: 0, adults: 0, seniors: 0};

      const oldest = dayjs(
        getOldestDate(datas.listNaissance.map(d => d.value)),
      ).format('YYYY-MM-DD');

      if (
        categorizedAges.childrens +
          categorizedAges.adults +
          categorizedAges.seniors >
        0
      ) {
        const selectedCategory = listCategories.find(
          c => c.value == datas.categoryCode,
        );
        const selectedDestination = listDestinations.find(
          d => d.value == datas.destinationCode,
        );
        if (selectedCategory && selectedDestination) {
          onSave({
            ...datas,
            idCategory: selectedCategory.id,
            idDestination: selectedDestination.id,
            dateNaissance: oldest,
            listNaissance: datas.listNaissance,
            categoryLabel: selectedCategory.label,
            destinationLabel: selectedDestination.label,
          });
        }
      } else {
        setModalData({
          status: true,
          title: 'Information',
          content: 'Veuillez renseigner au moins un(e) assuré(e)',
          btn1: 'OK',
          btn2: undefined,
          handleBtn1: () => setModalData({...initModalData}),
          handleBtn2: undefined,
        });
      }
    } else {
      console.log('errors', errors);
    }
  };

  const isFilled: () => boolean = () => {
    const listToVerify: Array<string> = [
      'adult',
      'categoryCode',
      'destinationCode',
      'enfant',
      'senior',
    ];

    let allOk: boolean = true;

    Object.entries(datas).map((element: any) => {
      if (listToVerify.includes(element[0]) && !element[1]) {
        allOk = false;
      }
    });

    if (allOk) {
      allOk = datas.listNaissance.length > 0;
    }

    return allOk;
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleInitValues = () => {
    setShowErrors({...showErrorValuesDefault});
  };

  const categories = React.useMemo(() => {
    return listCategories?.length
      ? [
          {label: 'Veuillez choisir votre catégorie', value: ''},
          ...listCategories,
        ]
      : [];
  }, [listCategories]);

  const destinations = React.useMemo(() => {
    const destinationLites =
      listDestinations?.length > 0
        ? listDestinations.filter(
            d => d.value !== 'Monde entier Exclu USA et Canada',
          )
        : [];

    return destinationLites?.length
      ? [
          {label: 'Veuillez choisir une destination', value: '', limit: 0},
          ...destinationLites.map((element: any) => ({
            label: element.label,
            value: element.value,
            limit: element.limit,
          })),
        ]
      : [];
  }, [listDestinations]);

  const ageLimit: number = React.useMemo(() => {
    return (
      destinations.length &&
      datas.destinationCode &&
      destinations.find(
        (destination: any) => destination.value === datas.destinationCode,
      )?.limit
    );
  }, [datas.destinationCode]);

  const ageList = React.useMemo<AgeCategory>(() => {
    const categorizedAge = datas.listNaissance
      ? countPersonsInAgeRanges(
          datas.listNaissance,
          {
            childrens: {
              min: dataConfig.ageGap.enfant.min,
              max: dataConfig.ageGap.enfant.max,
            },
            adults: {
              min: dataConfig.ageGap.adult.min,
              max: dataConfig.ageGap.adult.max,
            },
            seniors: {
              min: dataConfig.ageGap.senior.min,
              max: dataConfig.ageGap.senior.max,
            },
          },
          'value',
        )
      : {childrens: 0, adults: 0, seniors: 0};
    return {...categorizedAge};
  }, [datas.listNaissance]);

  return {
    handleChange,
    handleSubmit,
    handleCancel,
    handleInitValues,
    values: datas,
    errors,
    showErrors,
    isDisabled: !isFilled(),
    modalData,
    categories,
    destinations,
    ageLimit,
    ageList,
  };
};
